/**
 * Typed, fetch-based API client for the Umbrage Scribe API (see `openapi/scribe.yaml`).
 *
 * Platform-agnostic: takes an injectable `baseUrl` and an async `getToken()` token
 * provider so web (browser fetch) and native (React Native / Node fetch polyfills) can
 * both supply platform specifics without this package depending on either. The client
 * does not itself know how tokens are minted/refreshed — it just calls `getToken()`
 * once per request and sends it as a Bearer token.
 *
 * Auth model: a Microsoft Entra ID v2 access token (scoped to the Scribe API's own
 * app registration, never Microsoft Graph) travels as a Bearer token. Ownership/
 * authorization is resolved server-side from the token's `oid` claim — this client
 * never inspects or relies on token contents. See
 * docs/adr-001-shared-cognito-identity.md (superseded; now records the full-Entra-
 * direct decision).
 */
import { ScribeApiError, ScribeNetworkError } from "./errors.js";
export { ScribeApiError, ScribeNetworkError } from "./errors.js";
function buildQueryString(query) {
    if (!query)
        return "";
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null)
            continue;
        if (Array.isArray(value)) {
            for (const v of value)
                params.append(key, v);
        }
        else {
            params.append(key, String(value));
        }
    }
    const qs = params.toString();
    return qs ? `?${qs}` : "";
}
/**
 * Narrows a concrete `*Params` interface (declared without an index signature, for
 * good autocomplete/typo-checking at call sites) to the structural `QueryParamsLike`
 * shape `request()` expects. Every `*Params` interface in `types/index.ts` only ever
 * declares `QueryValue`-compatible properties, so this cast is safe; it exists purely
 * to work around TypeScript requiring an explicit index signature for structural
 * assignability to an indexed type, even when every member is compatible.
 */
function asQuery(params) {
    return params;
}
export class ScribeApiClient {
    baseUrl;
    getToken;
    fetchImpl;
    defaultHeaders;
    constructor(options) {
        this.baseUrl = options.baseUrl.replace(/\/+$/, "");
        this.getToken = options.getToken;
        // Bind to `globalThis` when falling back to the ambient `fetch` — browsers implement
        // `fetch` as a method requiring `this` to be the `Window`/`WorkerGlobalScope` it was
        // read off of ("Illegal invocation" otherwise), and storing the bare function as
        // `this.fetchImpl` here, then calling it as `this.fetchImpl(...)` below, detaches it
        // from that receiver. Every real-browser consumer of this client that doesn't pass an
        // explicit `fetch` override hit this: `request()` below always threw a
        // `ScribeNetworkError` wrapping "Illegal invocation" before a request ever reached the
        // network, indistinguishable at the call site from a genuine connectivity failure. A
        // caller-supplied `options.fetch` is trusted as-is (it may already be bound, or may be
        // a polyfill/test double with no `this` requirement at all).
        this.fetchImpl = options.fetch ?? (globalThis.fetch?.bind(globalThis));
        this.defaultHeaders = options.defaultHeaders ?? {};
        if (!this.fetchImpl) {
            throw new Error("ScribeApiClient: no fetch implementation available. Pass `fetch` explicitly on platforms without a global fetch.");
        }
    }
    async request(path, options = {}) {
        const url = `${this.baseUrl}${path}${buildQueryString(options.query)}`;
        const headers = { ...this.defaultHeaders };
        if (options.body !== undefined) {
            headers["content-type"] = "application/json";
        }
        if (!options.anonymous && this.getToken) {
            const token = await this.getToken();
            if (token)
                headers.authorization = `Bearer ${token}`;
        }
        const init = { method: options.method ?? "GET", headers };
        if (options.body !== undefined)
            init.body = JSON.stringify(options.body);
        if (options.signal !== undefined)
            init.signal = options.signal;
        let response;
        try {
            response = await this.fetchImpl(url, init);
        }
        catch (cause) {
            throw new ScribeNetworkError(`Network request to ${path} failed`, cause);
        }
        if (response.status === 204) {
            return undefined;
        }
        const text = await response.text();
        if (options.raw && response.ok) {
            return text;
        }
        const parsed = text.length > 0 ? safeJsonParse(text) : undefined;
        if (!response.ok) {
            // ERRORS-1: the API stamps `X-Request-Id` on every response (CORS-exposed), so a 5xx
            // with no JSON envelope — a crashed Lambda, an API Gateway error — still has an id.
            throw new ScribeApiError(response.status, parsed, response.headers?.get("X-Request-Id") ?? undefined);
        }
        return parsed;
    }
    // ── recordings ─────────────────────────────────────────────────────────────────
    async createRecording(body) {
        return this.request("/recordings", { method: "POST", body });
    }
    async listRecordings(params = {}) {
        return this.request("/recordings", { query: asQuery(params) });
    }
    async getRecording(id) {
        return this.request(`/recordings/${encodeURIComponent(id)}`);
    }
    /**
     * Deletes the recording and tags its stored audio + raw engine output for lifecycle
     * expiry: unreadable immediately, bytes permanently deleted about 31 days later (ADR-006).
     * Requires owner
     * permission. Throws `ScribeApiError` with code `storage_unavailable` (503) if storage
     * was unreachable, in which case nothing was deleted and a retry is safe.
     */
    async deleteRecording(id) {
        return this.request(`/recordings/${encodeURIComponent(id)}`, { method: "DELETE" });
    }
    /** Moves a recording to `folderId`, or removes it from its current folder if `null`. */
    async moveRecording(id, folderId) {
        return this.request(`/recordings/${encodeURIComponent(id)}`, {
            method: "PATCH",
            body: { folder_id: folderId },
        });
    }
    async getRecordingTranscript(id, params = {}) {
        return this.request(`/recordings/${encodeURIComponent(id)}/transcript`, {
            query: asQuery(params),
        });
    }
    /** Lists all transcript versions (metadata only), newest first. */
    async listRecordingTranscripts(id) {
        return this.request(`/recordings/${encodeURIComponent(id)}/transcripts`);
    }
    /** Word-level timings for a transcript version, grouped by stored segment (REPLAY-1).
     * Defaults to the authoritative version; pass `transcript_id` to target one. */
    async getTranscriptWords(id, params = {}) {
        return this.request(`/recordings/${encodeURIComponent(id)}/transcript/words`, { query: asQuery(params) });
    }
    async triggerTranscription(id, body) {
        return this.request(`/recordings/${encodeURIComponent(id)}/transcribe`, {
            method: "POST",
            body,
        });
    }
    /** Presigned, short-lived GET URL for playing back the recording's audio. */
    async getRecordingAudioUrl(id) {
        return this.request(`/recordings/${encodeURIComponent(id)}/audio`);
    }
    /** Lists the tags currently attached to a recording. */
    async getRecordingTags(id) {
        return this.request(`/recordings/${encodeURIComponent(id)}/tags`);
    }
    /** Lists AI artifacts derived from a recording, newest first. `kind` filters (e.g. "summary"). */
    async listRecordingArtifacts(id, kind) {
        return this.request(`/recordings/${encodeURIComponent(id)}/artifacts`, {
            query: asQuery({ kind }),
        });
    }
    /** Replaces the full set of tags on a recording with exactly `tagIds`. */
    async setRecordingTags(id, tagIds) {
        return this.request(`/recordings/${encodeURIComponent(id)}/tags`, {
            method: "PUT",
            body: { tag_ids: tagIds },
        });
    }
    /**
     * Bulk-edits segment text on the recording's current transcript. Every `segment_id`
     * must belong to the recording's current transcript, or the whole request is rejected.
     */
    async updateTranscriptSegments(id, body, params = {}) {
        return this.request(`/recordings/${encodeURIComponent(id)}/transcript/segments`, { method: "PATCH", body, query: asQuery(params) });
    }
    /**
     * Exports the recording's current transcript as a downloadable file. Returns the raw
     * file text — the server also sets a matching `Content-Type`/`Content-Disposition`,
     * but this fetch-agnostic client exposes only the body (see
     * `TRANSCRIPT_EXPORT_CONTENT_TYPES` in `../types/index.js` if a caller needs the
     * expected MIME type for the format it requested, e.g. to construct a client-side
     * `Blob` for download).
     */
    async exportTranscript(id, params = {}) {
        return this.request(`/recordings/${encodeURIComponent(id)}/transcript/export`, {
            query: asQuery(params),
            raw: true,
        });
    }
    // ── speakers ───────────────────────────────────────────────────────────────────
    /**
     * Renames and/or assigns a diarized speaker label to a real platform user. Both
     * fields on `body` are optional/independent; pass `null` to clear either.
     */
    async updateRecordingSpeaker(id, label, body) {
        return this.request(`/recordings/${encodeURIComponent(id)}/speakers/${encodeURIComponent(label)}`, { method: "PUT", body });
    }
    /**
     * Merges one or more diarized speakers into a single surviving label, reassigning
     * every affected segment's `speaker_label`.
     */
    async mergeRecordingSpeakers(id, body) {
        return this.request(`/recordings/${encodeURIComponent(id)}/speakers/merge`, { method: "POST", body });
    }
    // ── search ─────────────────────────────────────────────────────────────────────
    async searchTranscripts(params) {
        return this.request("/transcripts/search", { query: asQuery(params) });
    }
    // ── assistant ──────────────────────────────────────────────────────────────────
    /** Grounded Q&A over the caller's meetings (optionally scoped to one recording). */
    async askAssistant(body) {
        return this.request("/assistant/ask", { method: "POST", body });
    }
    // ── folders ────────────────────────────────────────────────────────────────────
    async listFolders(params = {}) {
        return this.request("/folders", { query: asQuery(params) });
    }
    async createFolder(body) {
        return this.request("/folders", { method: "POST", body });
    }
    async getFolder(id) {
        return this.request(`/folders/${encodeURIComponent(id)}`);
    }
    async updateFolder(id, body) {
        return this.request(`/folders/${encodeURIComponent(id)}`, {
            method: "PUT",
            body,
        });
    }
    async deleteFolder(id) {
        return this.request(`/folders/${encodeURIComponent(id)}`, { method: "DELETE" });
    }
    // ── tags ───────────────────────────────────────────────────────────────────────
    async listTags(params = {}) {
        return this.request("/tags", { query: asQuery(params) });
    }
    async createTag(body) {
        return this.request("/tags", { method: "POST", body });
    }
    async getTag(id) {
        return this.request(`/tags/${encodeURIComponent(id)}`);
    }
    async updateTag(id, body) {
        return this.request(`/tags/${encodeURIComponent(id)}`, { method: "PUT", body });
    }
    async deleteTag(id) {
        return this.request(`/tags/${encodeURIComponent(id)}`, { method: "DELETE" });
    }
    // ── users ──────────────────────────────────────────────────────────────────────
    /** Directory search (q) or batch resolve (ids) of platform users. */
    async listUsers(params) {
        return this.request("/users", { query: asQuery(params) });
    }
    /** The caller's own profile (platform users.id, display name, email, is_admin). */
    async getMe() {
        return this.request("/users/me");
    }
    // ── voice prints (docs/specs/voice-1.md) — 404 unless the feature is on ─────────────
    async getMyVoicePrint() {
        return this.request("/users/me/voice-print");
    }
    /** Create or replace the caller's print from their own speaker in a recording (202, pending). */
    async enrollMyVoicePrint(body) {
        return this.request("/users/me/voice-print", { method: "PUT", body });
    }
    async deleteMyVoicePrint() {
        await this.request("/users/me/voice-print", { method: "DELETE" });
    }
    /** Idempotent get-or-create of a platform users row for an Entra org member. */
    async provisionUser(body) {
        return this.request("/users/provision", { method: "POST", body });
    }
    // ── shares ─────────────────────────────────────────────────────────────────────
    async listShares(params) {
        return this.request("/shares", { query: asQuery(params) });
    }
    async createShare(body) {
        return this.request("/shares", { method: "POST", body });
    }
    async deleteShare(body) {
        return this.request("/shares", { method: "DELETE", body });
    }
    // ── vocabularies ───────────────────────────────────────────────────────────────
    async listVocabularies(params = {}) {
        return this.request("/vocabularies", { query: asQuery(params) });
    }
    async createVocabulary(body) {
        return this.request("/vocabularies", { method: "POST", body });
    }
    async replaceVocabularyTerms(body) {
        return this.request("/vocabularies", { method: "PUT", body });
    }
    async publishVocabulary(id) {
        return this.request(`/vocabularies/${encodeURIComponent(id)}/publish`, {
            method: "POST",
        });
    }
    /**
     * Delete a vocabulary — **admin only** (403 without the `Scribe.Admin` role). Removes the row,
     * its terms and versions, AND the AWS Transcribe vocabulary, so nothing engine-side is left
     * untracked.
     *
     * Recordings whose `job_spec.vocabulary_name` still names it keep that reference: it records
     * what was requested, and rewriting it would misreport what an existing transcript was
     * produced with. Callers rendering a re-transcribe control should detect the dangling name and
     * offer a replacement.
     */
    async deleteVocabulary(id) {
        await this.request(`/vocabularies/${encodeURIComponent(id)}`, { method: "DELETE" });
    }
    // ── webhooks ───────────────────────────────────────────────────────────────────
    async listWebhooks(params = {}) {
        return this.request("/webhooks", { query: asQuery(params) });
    }
    async createWebhook(body) {
        return this.request("/webhooks", { method: "POST", body });
    }
    async deleteWebhook(id) {
        return this.request(`/webhooks/${encodeURIComponent(id)}`, { method: "DELETE" });
    }
    // ── imports: Otter history (docs/specs/otter-import-1.md) ────────────────────────
    // All of these 404 when the server has the feature switched off.
    async getOtterImportStatus() {
        return this.request("/imports/otter");
    }
    /** Starts an OAuth connect; send the browser to `authorize_url`. */
    async connectOtter() {
        return this.request("/imports/otter/connect", { method: "POST" });
    }
    /** Forwards the `code`/`state` Otter appended to the web callback URL. */
    async completeOtterConnect(body) {
        return this.request("/imports/otter/callback", { method: "POST", body });
    }
    async disconnectOtter() {
        await this.request("/imports/otter/connection", { method: "DELETE" });
    }
    async startOtterImport() {
        return this.request("/imports/otter/runs", { method: "POST" });
    }
    async getOtterImportRun(runId) {
        return this.request(`/imports/otter/runs/${encodeURIComponent(runId)}`);
    }
    // ── transcribe-on-demand (docs/specs/ondemand-1.md) ──────────────────────────────
    // All but getTranscriptionSettings 404 unless the backend has the feature enabled.
    /** Always available; `feature_enabled: false` means hide every on-demand affordance. */
    async getTranscriptionSettings() {
        return this.request("/transcription-settings");
    }
    async updateTranscriptionSettings(body) {
        return this.request("/transcription-settings", { method: "PUT", body });
    }
    async listTranscriptionSubscriptions() {
        return this.request("/transcription-subscriptions");
    }
    async createTranscriptionSubscription(body) {
        return this.request("/transcription-subscriptions", {
            method: "POST",
            body,
        });
    }
    async updateTranscriptionSubscription(id, isActive) {
        return this.request(`/transcription-subscriptions/${encodeURIComponent(id)}`, { method: "PATCH", body: { is_active: isActive } });
    }
    async deleteTranscriptionSubscription(id) {
        await this.request(`/transcription-subscriptions/${encodeURIComponent(id)}`, {
            method: "DELETE",
        });
    }
    /** Side-effect free: where transcription/summary stand and why it will/won't auto-run. */
    async getRecordingTranscriptionStatus(id) {
        return this.request(`/recordings/${encodeURIComponent(id)}/transcription`);
    }
    /** "Opened": idempotently start whatever the recording is missing (transcript, else
     * summary). Returns immediately; poll the recording for completion. */
    async ensureRecordingTranscription(id, body = {}) {
        return this.request(`/recordings/${encodeURIComponent(id)}/transcription/ensure`, { method: "POST", body });
    }
    async setRecordingTranscriptionPolicy(id, policy) {
        return this.request(`/recordings/${encodeURIComponent(id)}/transcription-policy`, { method: "PUT", body: { policy } });
    }
    /** Search's "Transcribe these" — at most 10 ids. */
    async transcribeRecordingsBatch(body) {
        return this.request("/recordings/transcribe-batch", {
            method: "POST",
            body,
        });
    }
    /** Deferred recordings a full-text search cannot see (matched by title/tag/folder or date). */
    async searchUntranscribedRecordings(params = {}) {
        return this.request("/transcripts/search/untranscribed", {
            query: asQuery(params),
        });
    }
    // ── admin ──────────────────────────────────────────────────────────────────────
    /** Org + per-member usage/cost/budget-state for a month (default: current UTC month). */
    async getAdminUsage(month) {
        return this.request("/admin/usage", { query: asQuery(month ? { month } : {}) });
    }
    async getOrgSettings() {
        return this.request("/admin/settings");
    }
    async updateOrgSettings(body) {
        return this.request("/admin/settings", { method: "PATCH", body });
    }
    /** `monthlyBudgetUsd: null` stores an explicit-uncapped override for the member. */
    async setMemberBudget(userId, monthlyBudgetUsd) {
        return this.request(`/admin/members/${encodeURIComponent(userId)}/budget`, {
            method: "PUT",
            body: { monthly_budget_usd: monthlyBudgetUsd },
        });
    }
    // ── feature switches (docs/specs/features-1.md) ─────────────────────────────────
    /** Admin: every switchable feature with infrastructure status, runtime state and last change. */
    async listAdminFeatures() {
        return this.request("/admin/features");
    }
    /** Admin: switch a feature on/off. 409 when its infrastructure isn't deployed. */
    async updateFeatureSwitch(key, body) {
        return this.request(`/admin/features/${encodeURIComponent(key)}`, {
            method: "PATCH",
            body,
        });
    }
    /** Any signed-in user: `{key: enabled}` for gating UI. */
    async listFeatures() {
        return this.request("/features");
    }
    /**
     * Deletes any user's recording, tagging its objects for 30-day expiry (ADR-006).
     * Admin-only. `deleteRecording` above requires owner permission and there is no admin
     * bypass in the shared permission check, so this is the only way an admin can remove
     * someone else's recording.
     */
    async adminDeleteRecording(id) {
        return this.request(`/admin/recordings/${encodeURIComponent(id)}`, {
            method: "DELETE",
        });
    }
    /**
     * **Irreversibly** destroys a recording's stored audio and engine output NOW, instead of
     * leaving them to the 30-day expiry. Admin-only, and never the default path — prefer
     * `adminDeleteRecording` unless a retention window is genuinely unacceptable.
     */
    async purgeRecording(id) {
        return this.request(`/admin/recordings/${encodeURIComponent(id)}/purge`, {
            method: "POST",
        });
    }
    // ── support (ERRORS-1 Part B) ──────────────────────────────────────────────────
    /** Files an in-app bug report (stored as `pending`; a delivery worker opens the GitHub
     * issue). 404 when the backend's `SCRIBE_BUG_REPORTS` flag is off. */
    async createBugReport(body) {
        return this.request("/support/bug-reports", { method: "POST", body });
    }
    /** The exact, server-scrubbed issue that {@link createBugReport} would file. Stores nothing. */
    async previewBugReport(body) {
        return this.request("/support/bug-reports/preview", { method: "POST", body });
    }
    async getBugReport(id) {
        return this.request(`/support/bug-reports/${encodeURIComponent(id)}`);
    }
    /** The caller's own reports, newest first (optionally for one recording). */
    async listBugReports(params = {}) {
        return this.request("/support/bug-reports", { query: asQuery(params) });
    }
    /** Admin: every reporter's reports, newest first, one page. 404 while bug reports are off. */
    async listAdminBugReports(params = {}) {
        return this.request("/admin/bug-reports", { query: asQuery(params) });
    }
}
function safeJsonParse(text) {
    try {
        return JSON.parse(text);
    }
    catch {
        // Non-JSON body (e.g. an upstream proxy error page). Surface as a message string
        // so ScribeApiError still has something readable, without ever assuming shape.
        return { error: { code: "invalid_response", message: text.slice(0, 500) } };
    }
}
//# sourceMappingURL=index.js.map